import { Suspense } from 'react';

import { Route } from 'react-router-dom';

import { PageSkeleton } from '@gw/ui';
import { AppLayout } from '@/components/layouts/AppLayout';
import { lazyWithRetry } from '@/lib/lazyWithRetry.util';

// ============================================================================
// Public standalone pages: legal, payment results, gift-card redemption, and
// the referral landing handler. No auth. The payment result, gift-card
// redemption, and referral landing pages now render through the unified
// AppLayout shell's BARE (chromeless) variant — content only, no Sidebar,
// Header, or footer chrome. Mounted under RouteErrorBoundary in App.tsx.
// ============================================================================

// Public Pages
const AboutPage = lazyWithRetry(() =>
  import('@/pages/public/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const PrivacyPolicyPage = lazyWithRetry(() =>
  import('@/pages/public/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage }))
);
const TermsOfServicePage = lazyWithRetry(() =>
  import('@/pages/public/TermsOfServicePage').then((m) => ({ default: m.TermsOfServicePage }))
);
const CookiePreferencesPage = lazyWithRetry(() =>
  import('@/pages/public/CookiePreferencesPage').then((m) => ({
    default: m.CookiePreferencesPage,
  }))
);
const AcceptableUsePolicyPage = lazyWithRetry(() =>
  import('@/pages/public/AcceptableUsePolicyPage').then((m) => ({
    default: m.AcceptableUsePolicyPage,
  }))
);
// GW-1854: Referral link landing page handler
const ReferralLandingPage = lazyWithRetry(() =>
  import('@/pages/public/ReferralLandingPage').then((m) => ({
    default: m.ReferralLandingPage,
  }))
);
const PaymentCancelPage = lazyWithRetry(() =>
  import('@/pages/protected/PaymentCancelPage').then((m) => ({ default: m.PaymentCancelPage }))
);
const PaymentSuccessPage = lazyWithRetry(() =>
  import('@/pages/protected/PaymentSuccessPage').then((m) => ({ default: m.PaymentSuccessPage }))
);
const GiftCardRedeemPage = lazyWithRetry(() =>
  import('@/pages/tokens/GiftCardRedeemPage').then((m) => ({ default: m.GiftCardRedeemPage }))
);

export const publicRoutes = (
  <>
    {/* GW-10435/GW-10515: the public + four legal pages render through the
        unified AppLayout shell's DEFAULT variant. The shell auth-gates the
        sidebar ("sidebar iff authenticated"), so authenticated visitors get
        the full app chrome (with sidebar) and anonymous visitors get the
        header + footer chrome with no sidebar — no separate public variant is
        needed. hideSidebar is intentionally NOT passed. */}
    <Route element={<AppLayout variant="default" />}>
      {/* GW-10439: Public About page — folded into the PUBLIC variant group
          (shared Header + centered container main + AppFooter); no longer
          hand-rolls its own header/main. */}
      <Route
        path="/about"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <AboutPage />
          </Suspense>
        }
      />

      {/* Public Terms of Service page */}
      <Route
        path="/terms-of-service"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <TermsOfServicePage />
          </Suspense>
        }
      />

      {/* Public Privacy Policy page */}
      <Route
        path="/privacy-policy"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <PrivacyPolicyPage />
          </Suspense>
        }
      />

      {/* Public Acceptable Use Policy page (GW-481) */}
      <Route
        path="/acceptable-use-policy"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <AcceptableUsePolicyPage />
          </Suspense>
        }
      />

      {/* Public Cookie Preferences page (GW-139) */}
      <Route
        path="/cookie-settings"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <CookiePreferencesPage />
          </Suspense>
        }
      />
    </Route>

    {/* GW-10440: payment result, gift-card redemption, and referral landing
        pages render chromeless through AppLayout's BARE variant (no Sidebar,
        Header, or footer). Suspense stays per-child — never wrapping the
        group's Outlet. */}
    <Route element={<AppLayout variant="bare" />}>
      {/* GW-445: Public payment result pages - accessible without auth */}
      <Route
        path="/payment/success"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <PaymentSuccessPage />
          </Suspense>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <PaymentCancelPage />
          </Suspense>
        }
      />

      {/* GW-1581: Public gift card redemption page */}
      <Route
        path="/gift-cards/redeem"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <GiftCardRedeemPage />
          </Suspense>
        }
      />

      {/* GW-1854: Referral link landing page handler */}
      <Route
        path="/join"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <ReferralLandingPage />
          </Suspense>
        }
      />
    </Route>
  </>
);
